import { useEffect } from "react";
import { briefLlmInputsOf } from "../lib/report/briefLlmInputs.js";
import { prefetchBriefConclusion } from "./useBriefConclusion.js";
import { prefetchBriefBottomLine } from "./useBriefBottomLine.js";
import { prefetchInsightHeadings } from "./useInsightHeadings.js";

/**
 * Starts writing a report's brief lines — its headline, its "Why" and "The risk", and its
 * insight headings — as soon as the forecast on screen has its report, so they are cached (or
 * on their way) by the time the report is opened. Each is asked for once per report; the brief
 * then finds it in the same cache.
 *
 * Not gated on any user setting or forecaster: every report opens as the brief, whoever made
 * it, and briefLlmInputsOf degrades for an older report (no evidence cards or insights).
 *
 * @param {{ source: object|null, engine: string|null }} deps the forecast on screen and the
 *   forecaster that made it
 */
export function useBriefPrefetch({ source, engine }) {
  const r =
    source?.report ||
    source?.serverContext?.report ||
    source?.multi_report ||
    source?.contract?.multi_report ||
    null;
  // Keyed on the report itself: a new one (a reforecast, even at the same probability) is a
  // new object. Asking again for an unchanged report is free — answers are cached and a
  // request in flight is joined.
  useEffect(() => {
    if (!r) return;
    const inputs = briefLlmInputsOf(source, { engine });
    if (!inputs) return;
    if (inputs.conclusion) prefetchBriefConclusion(inputs.conclusion);
    prefetchBriefBottomLine(inputs.bottomLine);
    prefetchInsightHeadings(inputs.insights);
    // `source` is read for the report it holds, which `r` already tracks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, engine]);
}
